import React, { useState } from "react";
import { CheckIcon } from "../icons";
import FileCategorySelect from "./FileCategorySelect";

interface FileItem {
  id: string;
  displayName: string;
  description?: string;
  originalFileName: string;
  fileType: string;
  fileSize: number;
  category: string;
  defaultNote?: string;
  createdAt: string;
  uploadedBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface EditFileModalProps {
  file: FileItem;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    displayName: string;
    description: string;
    category: string;
  }) => Promise<void>;
  isLoading: boolean;
}

const EditFileModal: React.FC<EditFileModalProps> = ({
  file,
  isOpen,
  onClose,
  onSave,
  isLoading,
}) => {
  const [editData, setEditData] = useState({
    displayName: file.displayName,
    description: file.description || "",
    category: file.category,
  });

  const handleSave = async () => {
    await onSave(editData);
  };

  const handleCancel = () => {
    setEditData({
      displayName: file.displayName,
      description: file.description || "",
      category: file.category,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="text-center mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Edytuj plik: {file.originalFileName}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Rozmiar: {(file.fileSize / 1024 / 1024).toFixed(2)} MB •
              Przesłane: {new Date(file.createdAt).toLocaleDateString("pl-PL")}{" "}
              • Autor: {file.uploadedBy.firstName} {file.uploadedBy.lastName}
            </p>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nazwa wyświetlana *
              </label>
              <input
                type="text"
                value={editData.displayName}
                onChange={(e) =>
                  setEditData((prev) => ({
                    ...prev,
                    displayName: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Nazwa pliku widoczna w systemie"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Opis
              </label>
              <textarea
                value={editData.description}
                onChange={(e) =>
                  setEditData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Opcjonalny opis pliku"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategoria
              </label>
              <FileCategorySelect
                value={editData.category}
                onChange={(category: string) =>
                  setEditData((prev) => ({ ...prev, category }))
                }
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            >
              Anuluj
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !editData.displayName.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-700 border border-transparent rounded-md hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading && <CheckIcon className="h-4 w-4 animate-pulse" />}
              <span>{isLoading ? "Zapisywanie..." : "Zapisz zmiany"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditFileModal;
